import { useMemo, useCallback } from 'react';
import debounce from 'lodash-es/debounce';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import usePersistedState from 'shared/hooks/usePersistedState';
import { MessageType } from 'shared/types/graphql';
import { IChannelDraft } from '../interfaces/IChannelDraft';

interface IUseChannelDraftInput {
  channelId?: string;
}

interface IUseChannelDraftOutput {
  channelDraft: IChannelDraft;
  setChannelDraft: (channelDraft: Partial<IChannelDraft>) => void;
  clearChannelDraft: (channelId: string, channelDraft?: Partial<IChannelDraft>) => void;
}

const defaultChannelDraft = {
  messageType: MessageType.Message,
  draftMessageText: '',
  attachments: [],
  fillInTheBlankFormValues: [],
  timestamp: Date.now(),
};

const useChannelDraft = ({ channelId }: IUseChannelDraftInput): IUseChannelDraftOutput => {
  const [drafts, setDrafts] = usePersistedState<Record<string, IChannelDraft>>(
    PersistedStateKeys.ConversationDrafts,
    {},
  );

  const channelDraft = useMemo(() => {
    if (!channelId) return defaultChannelDraft;
    return drafts[channelId] || defaultChannelDraft;
  }, [channelId, drafts]);

  const setChannelDraft = useMemo(() => {
    return debounce((channelDraftUpdates: Partial<IChannelDraft>): void => {
      if (!channelId) return;
      setDrafts((drafts) => ({
        ...drafts,
        [channelId]: {
          ...(drafts[channelId] || defaultChannelDraft),
          ...channelDraftUpdates,
          timestamp: Date.now(),
        },
      }));
    }, 1000);
  }, [channelId, setDrafts]);

  const clearChannelDraft = useCallback(
    (channelId: string, updates?: Partial<Omit<IChannelDraft, 'timestamp'>>) => {
      setChannelDraft({ ...defaultChannelDraft, ...updates });
      setChannelDraft.flush();

      /**
       * We manually clear the draft message from local storage to account for when the user navigates
       * away from the conversation before the createChannelMessage mutation succeeds — the effect in
       * usePersistedState is not called in that case.
       */
      const drafts = JSON.parse(localStorage.getItem(PersistedStateKeys.ConversationDrafts) || '{}');
      const newValue = JSON.stringify({
        ...drafts,
        [channelId]: {
          ...defaultChannelDraft,
          ...updates,
          timestamp: Date.now(),
        },
      });
      localStorage.setItem(PersistedStateKeys.ConversationDrafts, newValue);
    },
    [setChannelDraft],
  );

  return { channelDraft, setChannelDraft, clearChannelDraft };
};

export default useChannelDraft;
